/**
 * "Build one for your bioregion" — the form on the project home.
 *
 * It posts to `/api/contact`, a Cloudflare Pages Function that verifies a
 * Turnstile token, rate-limits by address, and forwards the message to the
 * owner through Resend. The owner's address lives only in that function's
 * secrets; nothing in this bundle knows it (spec §4.3).
 *
 * Turnstile is loaded here, on mount, and nowhere else: the twin never pays
 * for a script it does not use. Without a site key (a local build) the form
 * says it is disabled rather than pretending to send.
 */
import { useEffect, useId, useRef, useState } from "react";

import { type ContactInput, type SendResult, submitContact, validateContact } from "./contact";

/** Read at render, not at import, so a test can stub it either way. */
function siteKey(): string | undefined {
  const k = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  return typeof k === "string" && k.length > 0 ? k : undefined;
}
const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible";
          callback: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        },
      ) => string;
      reset: (id?: string) => void;
      remove: (id?: string) => void;
    };
  }
}

type Phase = "idle" | "sending" | SendResult;

const EMPTY: ContactInput = { name: "", email: "", bioregion: "", message: "", optIn: false };

/** Loads the Turnstile script once and renders the widget into `el`. */
function useTurnstile(el: React.RefObject<HTMLDivElement | null>, key: string | undefined, onToken: (t: string | null) => void) {
  const widget = useRef<string | null>(null);
  useEffect(() => {
    if (!key || !el.current) return;
    let cancelled = false;
    const mount = () => {
      if (cancelled || !el.current || !window.turnstile || widget.current) return;
      // The flexible widget needs 300 px; below that (a 320 px phone with the
      // page's padding) it logs errors, so the compact one takes over.
      const size = el.current.getBoundingClientRect().width >= 300 ? "flexible" : "compact";
      widget.current = window.turnstile.render(el.current, {
        sitekey: key,
        theme: "dark",
        size,
        callback: (t) => onToken(t),
        "expired-callback": () => onToken(null),
        "error-callback": () => onToken(null),
      });
    };
    let poll: ReturnType<typeof setInterval> | null = null;
    if (window.turnstile) mount();
    else {
      const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
      const script = existing ?? document.createElement("script");
      script.addEventListener("load", mount, { once: true });
      if (!existing) {
        script.src = TURNSTILE_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      // A remount after the script already loaded would miss `load`; the
      // global is the truth, so watch for it too — for ten seconds, then give
      // up (a script that never arrives must not tick forever).
      let attempts = 0;
      poll = setInterval(() => {
        attempts += 1;
        if (window.turnstile || attempts >= 50) {
          if (poll) clearInterval(poll);
          poll = null;
          if (window.turnstile) mount();
        }
      }, 200);
    }
    return () => {
      cancelled = true;
      if (poll) clearInterval(poll);
      if (widget.current && window.turnstile) window.turnstile.remove(widget.current);
      widget.current = null;
    };
  }, [el, key, onToken]);
  return widget;
}

export function ContactForm() {
  const id = useId();
  const [input, setInput] = useState<ContactInput>(EMPTY);
  const [errors, setErrors] = useState<Partial<Record<keyof ContactInput, string>>>({});
  const [phase, setPhase] = useState<Phase>("idle");
  const [token, setToken] = useState<string | null>(null);
  /** An error about the form as a whole (the check, not a field). */
  const [formError, setFormError] = useState<string | null>(null);
  const key = siteKey();
  const turnstileEl = useRef<HTMLDivElement | null>(null);
  const widget = useTurnstile(turnstileEl, key, setToken);

  const set = (k: keyof ContactInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const v = e.target instanceof HTMLInputElement && e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setInput((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((s) => ({ ...s, [k]: undefined }));
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    const v = validateContact(input);
    if (!v.ok) {
      setErrors(v.errors);
      return;
    }
    if (!token) {
      setFormError("The check above has not finished yet — give it a second, then send again.");
      return;
    }
    // The honeypot is read from the DOM, never from state: a person never
    // touches it, a script filling every input does.
    const website = String(new FormData(e.currentTarget).get("website") ?? "");
    setPhase("sending");
    const result = await submitContact(v.input, token, website);
    setPhase(result);
    if (result === "sent") setInput(EMPTY);
    if (window.turnstile && widget.current) {
      window.turnstile.reset(widget.current);
      setToken(null);
    }
  };

  if (!key) {
    return (
      <p className="contact__disabled" data-testid="contact-disabled">
        The form is disabled in this build: it needs the site's verification key to send.
      </p>
    );
  }

  const field = (k: Exclude<keyof ContactInput, "optIn">, label: string, extra?: React.ReactNode) => (
    <div className={`field${errors[k] ? " field--error" : ""}`}>
      <label className="field__label" htmlFor={`${id}-${k}`}>
        {label}
        {extra}
      </label>
      {k === "message" ? (
        <textarea id={`${id}-${k}`} className="field__input" rows={6} value={input[k]} onChange={set(k)} required aria-describedby={errors[k] ? `${id}-${k}-err` : undefined} />
      ) : (
        <input
          id={`${id}-${k}`}
          className="field__input"
          type={k === "email" ? "email" : "text"}
          autoComplete={k === "email" ? "email" : k === "name" ? "name" : "off"}
          value={input[k]}
          onChange={set(k)}
          required={k !== "bioregion"}
          aria-describedby={errors[k] ? `${id}-${k}-err` : undefined}
        />
      )}
      {errors[k] ? (
        <p className="field__error" id={`${id}-${k}-err`} role="alert">
          {errors[k]}
        </p>
      ) : null}
    </div>
  );

  return (
    <form className="contact" onSubmit={onSubmit} noValidate data-testid="contact-form">
      <div className="contact__row">
        {field("name", "Your name")}
        {field("email", "Email")}
      </div>
      {field("bioregion", "Your bioregion", <span className="field__hint"> — the watershed, range or valley you mean</span>)}
      {field("message", "What a twin would do for it")}
      {/* Honeypot: off-screen, unlabeled to people, tempting to a script. */}
      <div className="contact__hp" aria-hidden="true">
        <label htmlFor={`${id}-website`}>Website</label>
        <input id={`${id}-website`} name="website" type="text" tabIndex={-1} autoComplete="off" defaultValue="" />
      </div>
      <label className="check">
        <input type="checkbox" checked={input.optIn} onChange={set("optIn")} />
        <span>Send me the weekly Front Range briefing</span>
      </label>
      <div className="contact__verify" ref={turnstileEl} />
      {formError ? (
        <p className="field__error" role="alert" data-testid="contact-form-error">
          {formError}
        </p>
      ) : null}
      <div className="contact__actions">
        <button className="btn btn--primary" type="submit" disabled={phase === "sending"}>
          {phase === "sending" ? "Sending…" : "Send"}
        </button>
        <p className="contact__status" role="status" aria-live="polite">
          {phase === "sent" ? "Sent. I'll write back." : null}
          {phase === "rate_limited" ? "That's a few messages in an hour from here — try again later." : null}
          {phase === "rejected" ? "The check didn't pass. Reload and try once more." : null}
          {phase === "failed" ? "Couldn't send just now. Try again in a minute." : null}
        </p>
      </div>
    </form>
  );
}
